import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { EntRole } from "./role";
import { RootState } from "shared/store/root-state.interface";
import { getCookie } from "typescript-cookie";
import dayjs from "dayjs";
import { EntUserRegister } from "./userRegister";
import axios from "axios";
import { getIdentityBaseUrl } from "features/auth/lib/identity";

export const AUTH_SLICE_KEY = "authSlice";

interface Messages {
  messageKy: string;
  messageRu: string;
}

export interface AuthState {
  isLoggedIn: boolean;
  changePassword: boolean;
  hasValidToken: boolean;
  accessToken: string;
  refreshToken: string;
  pin: string;
  id: number;
  email: string;
  patronymic: string;
  phone: string;
  secondName: string;
  name: string;
  passwordChangeNextLogon: boolean;
  password: string;
  temporaryAccessUntilTime: string;
  roleIds: number[];
  errorMessage: string;
  errorMessageReg: string;
  createdTime: string;
  roles: EntRole[] | null;
  registrationUserId: string | null;
  userRole: string | null;
  supplierProfile: EntUserRegister | null;
  oauthUrl: string | null;
  oauthState: string | null;
}

const lsToken = localStorage.getItem("token") || "";
const lsRefresh = localStorage.getItem("refreshToken") || "";
const lsIsLogged = localStorage.getItem("isLoggedIn") === "true";

const initialState: AuthState = {
  hasValidToken: !!lsToken,
  isLoggedIn: lsIsLogged,
  changePassword: localStorage.getItem("changePassword") === "true",
  userRole: localStorage.getItem("userRole"),
  accessToken: lsToken,
  refreshToken: lsRefresh,
  pin: "",
  id: 0,
  email: "",
  patronymic: "",
  phone: "",
  secondName: "",
  name: "",
  password: "",
  createdTime: dayjs().format("DD.MM.YYYY"),
  passwordChangeNextLogon: false,
  temporaryAccessUntilTime: "",
  roleIds: [],
  errorMessage: "",
  errorMessageReg: "",
  roles: null,
  registrationUserId: null,
  supplierProfile: null,
  oauthUrl: null,
  oauthState: null,
};

interface ILoginResponse {
  success: boolean;
  msg: Messages;
  error: string;
  res: {
    accessToken: string;
    refreshToken: string;
  };
  t: Date;
  v: string;
}

interface IAuthParams {
  username: string;
  password: string;
}

export class AuthSliceMethods {
  static errorMessage: string = "";
  static errorMessageReg: string = "";

  static login = createAsyncThunk(
    "user/login",
    async (params: IAuthParams): Promise<ILoginResponse> => {
      try {
        const identity = getIdentityBaseUrl();
        const res = await axios.post<ILoginResponse>(
          `${identity}/user/login`,
          { username: params.username, password: params.password },
          { headers: { "Content-Type": "application/json;charset=utf-8" } }
        );
        return res.data;
      } catch (ex: any) {
        const isKyrgyz = getCookie("i18next") === "ky";
        const lang = isKyrgyz ? "kg" : "ru";
        const res = ex.response?.data?.res;
        let errorMessage = "Неизвестная ошибка";

        if (res && typeof res === "object" && res.ru) {
          errorMessage = res[lang] || res.ru;
        } else if (Array.isArray(res)) {
          errorMessage = res.map((e: any) => e[lang] || e.ru).join(", \n");
        } else if (typeof res === "string") {
          errorMessage = res;
        } else if (ex.response?.data?.msg) {
          errorMessage = ex.response.data.msg;
        }

        this.errorMessage = errorMessage;
        return Promise.reject(ex);
      }
    }
  );

  static logout = createAsyncThunk("auth/logout", async () => {
    try {
      // optional: await axiosApiInstance.post('ap/logout');
    } catch {}
  });
}

export const authSlice = createSlice({
  name: AUTH_SLICE_KEY,
  initialState,
  reducers: {
    setSupplierProfile(
      state,
      action: PayloadAction<AuthState["supplierProfile"]>
    ) {
      state.supplierProfile = action.payload;
    },
    clearAuthCredentials: (state) => {
      localStorage.removeItem("isLoggedIn");
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("tokenRefreshing");
      localStorage.removeItem("userRole");
      state.pin = "";
      state.email = "";
      state.patronymic = "";
      state.phone = "";
      state.name = "";
      state.id = 0;
      state.secondName = "";
      state.accessToken = "";
      state.refreshToken = "";
      state.isLoggedIn = false;
      state.hasValidToken = false;
      state.roles = null;
      state.userRole = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(AuthSliceMethods.login.fulfilled, (state, action) => {
        if (action.payload?.res === null || !action.payload?.res?.accessToken) {
          state.errorMessage = "Проверьте правильность введенного пароля";
        } else {
          localStorage.setItem("isLoggedIn", "true");
          localStorage.setItem("token", action.payload.res.accessToken);
          localStorage.setItem("refreshToken", action.payload.res.refreshToken);
          state.isLoggedIn = true;
          state.hasValidToken = true;
          state.accessToken = action.payload.res.accessToken;
          state.refreshToken = action.payload.res.refreshToken;
          state.email = "";
          state.pin = "";
          state.patronymic = "";
          state.name = "";
          state.phone = "";
          state.id = 0;
          state.secondName = "";
          state.roles = null;
          state.errorMessage = "";
        }
      })
      .addCase(AuthSliceMethods.logout.fulfilled, (state) => {
        localStorage.removeItem("isLoggedIn");
        localStorage.removeItem("changePassword");
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("tokenRefreshing");
        localStorage.removeItem("userRole");
        state.pin = "";
        state.email = "";
        state.patronymic = "";
        state.name = "";
        state.phone = "";
        state.id = 0;
        state.secondName = "";
        state.accessToken = "";
        state.refreshToken = "";
        state.isLoggedIn = false;
        state.hasValidToken = false;
        state.roles = null;
        state.userRole = null;
        state.errorMessage = "";
        state.errorMessageReg = "";
      })

      .addCase(AuthSliceMethods.login.rejected, (state) => {
        state.errorMessage = AuthSliceMethods.errorMessage;
      });
  },
});

export const getAuthState = (rootState: RootState): AuthState =>
  rootState[AUTH_SLICE_KEY];

export const extractCodeFromCallbackUrl = (
  callbackUrl: string
): { code: string; state: string } | null => {
  try {
    const url = new URL(callbackUrl);
    const code = url.searchParams.get("code");
    const state = url.searchParams.get("state");

    if (code && state) {
      return { code, state };
    }
    return null;
  } catch (error) {
    console.error("Ошибка извлечения параметров из callback URL:", error);
    return null;
  }
};
